/**
 * Vista Series — tabla read-only filtrable.
 *
 * Filtros: GTIN, lote, ubicación, tenencia, estado de traza SNT (derivado),
 * búsqueda libre por ID. Estado SNT se deriva en runtime con
 * derivarEstadoTrazaSerie (sin persistir — ver Fase 1).
 */

import { useMemo, useState } from 'react';
import { Search, Package, GitBranch } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useSeries,
  useMovimientos,
  useOperaciones,
  useProductos,
  useUbicaciones,
} from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { fmtDate } from '@/lib/format';
import { derivarEstadoTrazaSerie } from '@/domain/traza';
import type { EstadoTrazaSerie, TenenciaSerie } from '@/domain/types';

const TRAZA_LABEL: Record<EstadoTrazaSerie, string> = {
  trazada: 'Trazada',
  pendiente: 'Pendiente SNT',
  solo_canceladas: 'Sin traza vigente',
  nunca_trazada: 'No trazada',
};
const TRAZA_BADGE: Record<EstadoTrazaSerie, string> = {
  trazada: 'badge-green',
  pendiente: 'badge-blue',
  solo_canceladas: 'badge-gray',
  nunca_trazada: 'badge-yellow',
};
// "Bajo tenencia" en vez de "En stock" — el sistema solo conoce series
// que pasaron por SNT, no el stock físico del depósito. El término preciso
// regulatoriamente es "tenencia" (ANMAT lo usa explícitamente).
const TENENCIA_LABEL: Record<TenenciaSerie, string> = {
  en_poder: 'Bajo tenencia',
  en_transito: 'En tránsito',
  entregada: 'Entregada',
  destruida: 'Destruida',
};
const TENENCIA_BADGE: Record<TenenciaSerie, string> = {
  en_poder: 'badge-green',
  en_transito: 'badge-blue',
  entregada: 'badge-gray',
  destruida: 'badge-red',
};

export function SeriesPage() {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: series = [], isLoading, error } = useSeries();
  const { data: movimientos = [] } = useMovimientos();
  const { data: operaciones = [] } = useOperaciones();
  const { data: productos = [] } = useProductos();
  const { data: ubicaciones = [] } = useUbicaciones();

  const [filtroGtin, setFiltroGtin] = useState('all');
  const [filtroLote, setFiltroLote] = useState('');
  const [filtroTenencia, setFiltroTenencia] = useState('all');
  const [filtroUbicacion, setFiltroUbicacion] = useState('mine');
  const [filtroTraza, setFiltroTraza] = useState('all');
  const [busqueda, setBusqueda] = useState('');

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return series.filter((s) => {
      if (filtroGtin !== 'all' && s.gtin !== filtroGtin) return false;
      if (filtroLote && !s.lote.toLowerCase().includes(filtroLote.toLowerCase())) return false;
      if (filtroTenencia !== 'all' && s.tenencia !== filtroTenencia) return false;
      if (filtroUbicacion === 'mine' && s.ubicacionActualId !== ubicacionActivaId) return false;
      if (filtroUbicacion !== 'mine' && filtroUbicacion !== 'all' && s.ubicacionActualId !== filtroUbicacion) return false;
      if (filtroTraza !== 'all') {
        const traza = derivarEstadoTrazaSerie(s, movimientos, operaciones);
        if (traza !== filtroTraza) return false;
      }
      if (!q) return true;
      return s.id.toLowerCase().includes(q);
    });
  }, [series, filtroGtin, filtroLote, filtroTenencia, filtroUbicacion, filtroTraza, busqueda, ubicacionActivaId, movimientos, operaciones]);

  if (isLoading) {
    return <TableSkeleton rows={8} columns={6} caption="Cargando series..." />;
  }
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-everex-azul">
          <Package className="h-5 w-5" />
          Series del inventario
        </CardTitle>
        <p className="text-xs text-gray-600">
          Tabla filtrable de todas las series. El estado de traza SNT se deriva en runtime
          mirando movimientos + operaciones (no se persiste — patrón v8 del mockup).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <div className="md:col-span-2">
            <Label className="mb-1 block text-xs">Buscar (N° de serie)</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
              <Input
                className="pl-9 font-mono"
                placeholder="SN-2026-..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-xs">Producto</Label>
            <Select value={filtroGtin} onValueChange={setFiltroGtin}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {productos.map((p) => (
                  <SelectItem key={p.gtin} value={p.gtin}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs">Lote</Label>
            <Input value={filtroLote} onChange={(e) => setFiltroLote(e.target.value)} placeholder="BIR-2026A..." />
          </div>
          <div>
            <Label className="mb-1 block text-xs">Tenencia</Label>
            <Select value={filtroTenencia} onValueChange={setFiltroTenencia}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {(Object.keys(TENENCIA_LABEL) as TenenciaSerie[]).map((t) => (
                  <SelectItem key={t} value={t}>{TENENCIA_LABEL[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs">Ubicación</Label>
            <Select value={filtroUbicacion} onValueChange={setFiltroUbicacion}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mine">Mi GLN activo</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
                {ubicaciones.filter((u) => !u.externa).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="mb-1 block text-xs">Estado SNT (derivado)</Label>
          <Select value={filtroTraza} onValueChange={setFiltroTraza}>
            <SelectTrigger className="md:w-1/4"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {(Object.keys(TRAZA_LABEL) as EstadoTrazaSerie[]).map((t) => (
                <SelectItem key={t} value={t}>{TRAZA_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="text-[11px] text-gray-600">
          {filtradas.length} de {series.length} series (mostrando primeras 200)
        </div>

        <div className="overflow-x-auto rounded border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Serie</TableHead>
                <TableHead>Producto</TableHead>
                <TableHead>Lote / Vto</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Tenencia</TableHead>
                <TableHead>SNT</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtradas.slice(0, 200).map((s) => {
                const traza = derivarEstadoTrazaSerie(s, movimientos, operaciones);
                const prod = productos.find((p) => p.gtin === s.gtin);
                const ub = ubicaciones.find((u) => u.id === s.ubicacionActualId);
                return (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.id}</TableCell>
                    <TableCell className="text-xs">{prod?.nombre ?? s.gtin}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-mono">{s.lote}</div>
                      <div className="text-[11px] text-gray-600">{s.vencimiento}</div>
                    </TableCell>
                    <TableCell className="text-xs">{ub?.nombre ?? '—'}</TableCell>
                    <TableCell>
                      <span className={cn('badge', TENENCIA_BADGE[s.tenencia])}>
                        {TENENCIA_LABEL[s.tenencia]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={cn('badge', TRAZA_BADGE[traza])}>
                        {TRAZA_LABEL[traza]}
                      </span>
                    </TableCell>
                    <TableCell className="text-[11px]">
                      {s.altaManual ? '✍ Alta manual' : '🏭 Producción'}
                    </TableCell>
                    <TableCell className="text-[11px] text-gray-600">
                      {fmtDate(s.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/trazabilidad?serie=${encodeURIComponent(s.id)}`}
                        className="inline-flex items-center gap-1 text-[11px] text-everex-azul hover:underline"
                        title="Ver timeline de trazabilidad"
                      >
                        <GitBranch className="h-3 w-3" />
                        Ver
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
